'use client';

import type { RefObject } from 'react';
import { useTranslations } from 'next-intl';
import { Box, VStack } from '@terra/ui-styled-system';
import { Button } from '../../../components/button';
import { ContentCard } from '../../content-card/ContentCard';
import type { DocumentationFileCategory } from './applicationDocumentation.types';
import type { ApplicationDocumentationViewModel } from './applicationDocumentationView.types';
import { ApiDocumentationRequiredBadge } from './ApiDocumentationRequiredBadge';
import { ApplicationDocumentationCategoryFiles } from './ApplicationDocumentationCategoryFiles';
import { DocumentationAddMenu } from './DocumentationAddMenu';
import { DocumentationSectionHeader } from './DocumentationSectionHeader';

const SectionDivider = () => (
  <Box
    w="full"
    borderTopWidth="1px"
    borderTopStyle="solid"
    borderColor="border.neutral.default"
    my={3}
  />
);

type ApplicationDocumentationSectionsProps = {
  viewModel: ApplicationDocumentationViewModel;
  hideHeader?: boolean;
  showApiRequiredBadge?: boolean;
  apiSectionRef: RefObject<HTMLDivElement | null>;
  testingReportsSectionRef: RefObject<HTMLDivElement | null>;
};

export const ApplicationDocumentationSections = ({
  viewModel,
  hideHeader,
  showApiRequiredBadge,
  apiSectionRef,
  testingReportsSectionRef,
}: ApplicationDocumentationSectionsProps) => {
  const t = useTranslations(
    'designSystem.documentsReports.applicationDocumentation',
  );
  const {
    visibleFilesByCategory,
    visibleDocumentationUrls,
    isReadOnly,
    isUploading,
    busySection,
    onOpenFileDialog,
    onOpenLinksDialog,
  } = viewModel;
  const applicationBlockTopPadding =
    visibleFilesByCategory.application.length +
      visibleDocumentationUrls.length >
    0
      ? 3
      : 0;
  const addFilesButton = (category: DocumentationFileCategory) =>
    isReadOnly ? null : (
      <Button
        variant="ghost"
        size="sm"
        fontWeight="normal"
        disabled={isUploading}
        onClick={() => onOpenFileDialog(category)}
      >
        {t('addFiles')}
      </Button>
    );

  return (
    <ContentCard title={hideHeader ? undefined : t('title')}>
      <VStack gap={0} alignItems="stretch" w="full">
        <DocumentationSectionHeader
          label={t('categories.applicationDocumentation')}
          isBusy={busySection === 'application'}
          action={
            isReadOnly ? null : (
              <DocumentationAddMenu
                triggerVariant="add-documentation"
                disabled={isUploading}
                onAddFiles={() => onOpenFileDialog('application')}
                onAddLinks={onOpenLinksDialog}
              />
            )
          }
        />
        <VStack
          alignItems="stretch"
          gap={3}
          w="full"
          pt={applicationBlockTopPadding}
        >
          <ApplicationDocumentationCategoryFiles
            category="application"
            viewModel={viewModel}
          />
        </VStack>

        <SectionDivider />

        <DocumentationSectionHeader
          containerRef={apiSectionRef}
          label={t('categories.apiDocumentation')}
          badge={
            showApiRequiredBadge ? <ApiDocumentationRequiredBadge /> : null
          }
          isBusy={busySection === 'api'}
          action={addFilesButton('api')}
        />
        <Box pt={3} _empty={{ pt: 0 }}>
          <ApplicationDocumentationCategoryFiles
            category="api"
            viewModel={viewModel}
          />
        </Box>

        <SectionDivider />

        <DocumentationSectionHeader
          containerRef={testingReportsSectionRef}
          label={t('categories.previousTestingReports')}
          isBusy={busySection === 'testing-reports'}
          action={addFilesButton('testing-reports')}
        />
        <Box pt={3} _empty={{ pt: 0 }}>
          <ApplicationDocumentationCategoryFiles
            category="testing-reports"
            viewModel={viewModel}
          />
        </Box>
      </VStack>
    </ContentCard>
  );
};
